import { ref, computed, Ref } from 'vue'
import api from '@/api'
import { WhatsAppConfiguration, GroupInfo, MessageTemplate } from '@/classes/WhatsApp'
import util from '@/helpers/util'

export type ConfigWithExtras = WhatsAppConfiguration & {
    taglineOverride?: string
    generatedMessage?: string
    editedMessage?: string
    displayMessage?: string
    mediaUrl?: string | null
    mediaIsImage?: boolean
    isUploadingMedia?: boolean
    sendMode?: string
    scheduledDateTime?: string
    isSendingToQueue?: boolean
    skipPreview?: boolean
    delayHours?: number
    delayMinutes?: number
    delaySeconds?: number
    selectedTemplateName?: string
}

export function useWhatsAppConfigurations(allGroups: Ref<GroupInfo[]>) {
    const availableConfigurations = ref<WhatsAppConfiguration[]>([])
    const selectedConfig = ref<WhatsAppConfiguration | null>(null)
    const isLoadingConfiguration = ref(false)
    const isSavingConfiguration = ref(false)
    const openConfigurations = ref<ConfigWithExtras[]>([])
    const showCreateConfigModal = ref(false)
    const editingConfig = ref<WhatsAppConfiguration | null>(null)
    const editingConfigIndex = ref<number | null>(null)
    const configGroupsExpanded = ref<Record<number, boolean>>({})

    const configToConfigWithExtras = (config: WhatsAppConfiguration | Partial<WhatsAppConfiguration>): ConfigWithExtras => {
        // Create a new config instance to avoid modifying the original
        const configCopy = config instanceof WhatsAppConfiguration 
            ? new WhatsAppConfiguration(config)
            : new WhatsAppConfiguration(config as any)
        
        // Migrate defaultMessage to messageTemplates if templates array is empty
        if ((!configCopy.messageTemplates || configCopy.messageTemplates.length === 0) && configCopy.defaultMessage) {
            if (!configCopy.messageTemplates) {
                configCopy.messageTemplates = []
            }
            configCopy.messageTemplates.push(new MessageTemplate({ name: 'default', content: configCopy.defaultMessage }))
        }

        // Determine selected template name (first template or 'default')
        const selectedTemplateName = configCopy.messageTemplates && configCopy.messageTemplates.length > 0
            ? configCopy.messageTemplates[0].name
            : 'default'

        // Get the content of the selected template
        const selectedTemplate = configCopy.messageTemplates?.find(t => t.name === selectedTemplateName)
        const templateContent = selectedTemplate?.content || configCopy.defaultMessage || ''

        // Use Object.assign to preserve the instance methods
        return Object.assign(configCopy, {
            taglineOverride: '',
            generatedMessage: '',
            editedMessage: templateContent,
            displayMessage: '',
            mediaUrl: null,
            mediaIsImage: false,
            isUploadingMedia: false,
            sendMode: 'now',
            scheduledDateTime: '',
            isSendingToQueue: false,
            skipPreview: false,
            delayHours: undefined,
            delayMinutes: undefined,
            delaySeconds: undefined,
            selectedTemplateName: selectedTemplateName
        }) as ConfigWithExtras
    }

    const getConfigGroupsCount = (config: WhatsAppConfiguration | ConfigWithExtras): number => {
        return config.groups.length
    }

    const getAccessibleGroupsCount = (config: WhatsAppConfiguration | ConfigWithExtras): number => {
        return config.groups.filter(group => allGroups.value.some(g => g.jid === group.jid)).length
    }

    const getUnaccessibleGroupsCount = (config: WhatsAppConfiguration | ConfigWithExtras): number => {
        return config.groups.filter(group => !allGroups.value.some(g => g.jid === group.jid)).length
    }

    const toggleConfigGroups = (index: number) => {
        configGroupsExpanded.value[index] = !configGroupsExpanded.value[index]
    }

    const loadConfigurations = async () => {
        try {
            selectedConfig.value = null
            const response = await api.whatsapp.getConfigurations()
            availableConfigurations.value = response.data.map((config: any) => new WhatsAppConfiguration(config))
        } catch (error) {
            console.error('Failed to load configurations:', error)
        }
    }

    const addSelectedConfiguration = () => {
        if (!selectedConfig.value) return
        // Ensure we have a proper WhatsAppConfiguration instance
        const configInstance = selectedConfig.value instanceof WhatsAppConfiguration 
            ? selectedConfig.value 
            : new WhatsAppConfiguration(selectedConfig.value as any)
        const configWithExtras = configToConfigWithExtras(configInstance)
        openConfigurations.value.push(configWithExtras)
        selectedConfig.value = null
    }

    const resetSelectedConfiguration = (config: WhatsAppConfiguration | ConfigWithExtras) => {
        const configInstance = config instanceof WhatsAppConfiguration 
            ? config 
            : new WhatsAppConfiguration(config as any)
        const index = openConfigurations.value.findIndex(c => c.id === configInstance.id)
        if (index >= 0) {
            openConfigurations.value[index] = configToConfigWithExtras(configInstance)
        }
    }

    const editSelectedConfiguration = () => {
        if (!selectedConfig.value) return
        editingConfigIndex.value = availableConfigurations.value.findIndex(c => c.id === selectedConfig.value?.id)
        editingConfig.value = new WhatsAppConfiguration(selectedConfig.value as any)
        showCreateConfigModal.value = true
    }

    const deleteSelectedConfiguration = async () => {
        if (!selectedConfig.value || isLoadingConfiguration.value) return

        if (!confirm(`Are you sure you want to delete configuration "${selectedConfig.value.name}"? This action cannot be undone.`)) {
            return
        }

        isLoadingConfiguration.value = true
        try {
            await api.whatsapp.deleteConfiguration(selectedConfig.value.id)

            const openIndex = openConfigurations.value.findIndex(c => c.id === selectedConfig.value!.id)
            if (openIndex >= 0) {
                openConfigurations.value.splice(openIndex, 1)
            }

            await loadConfigurations()
            selectedConfig.value = null
            util.showToast('Configuration deleted successfully', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error: any) {
            console.error('Failed to delete configuration:', error)
            const errorMsg = error.response?.data?.error || error.message || 'Failed to delete configuration'
            util.showToast(`Failed to delete configuration: ${errorMsg}`, { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            isLoadingConfiguration.value = false
        }
    }

    const closeConfiguration = (index: number) => {
        openConfigurations.value.splice(index, 1)
    }

    const openCreateConfigModal = () => {
        editingConfigIndex.value = null
        editingConfig.value = new WhatsAppConfiguration()
        editingConfig.value.defaultMessage = '*{{tagline}}*\n\n{{title}}\n\n{{price}}\n\n👉 {{url}}'
        editingConfig.value.defaultTagLine = 'Great Deal!'
        editingConfig.value.groups = []
        // Initialize with a default template
        editingConfig.value.messageTemplates = [
            new MessageTemplate({
                name: 'default',
                content: '*{{tagline}}*\n\n{{title}}\n\n{{price}}\n\n👉 {{url}}'
            })
        ]
        showCreateConfigModal.value = true
    }

    const editConfiguration = (config: WhatsAppConfiguration | ConfigWithExtras) => {
        editingConfigIndex.value = availableConfigurations.value.findIndex(c => c.id === config.id)
        // Create a clean WhatsAppConfiguration instance (without extras)
        const configData: Partial<WhatsAppConfiguration> = {
            id: config.id,
            name: config.name,
            groups: config.groups,
            defaultMessage: config.defaultMessage,
            defaultTagLine: config.defaultTagLine,
            uniqueIdPrefix: config.uniqueIdPrefix,
            messageTemplates: ('messageTemplates' in config ? config.messageTemplates : []) || []
        }
        editingConfig.value = new WhatsAppConfiguration(configData as any)
        showCreateConfigModal.value = true
    }

    const addGroupToConfig = (group: GroupInfo, config: WhatsAppConfiguration | ConfigWithExtras) => {
        config.groups.push(group)
    }

    const removeGroupFromConfig = (group: GroupInfo, config: WhatsAppConfiguration | ConfigWithExtras) => {
        config.groups = config.groups.filter(g => g.jid !== group.jid)
    }

    const addTemplateToConfig = (config: WhatsAppConfiguration | ConfigWithExtras) => {
        if (!config.messageTemplates) {
            config.messageTemplates = []
        }
        const newTemplate = new MessageTemplate({
            name: `Template ${config.messageTemplates.length + 1}`,
            content: '*{{tagline}}*\n\n{{title}}\n\n{{price}}\n\n👉 {{url}}'
        })
        config.messageTemplates.push(newTemplate)
    }

    const removeTemplateFromConfig = (templateIndex: number, config: WhatsAppConfiguration | ConfigWithExtras) => {
        if (config.messageTemplates && config.messageTemplates.length > templateIndex) {
            config.messageTemplates.splice(templateIndex, 1)
        }
    }

    const ensureTemplateName = (template: MessageTemplate, templateIndex: number, config: WhatsAppConfiguration | ConfigWithExtras) => {
        if (!template.name || template.name.trim() === '') {
            template.name = `Template ${templateIndex + 1}`
        }
        // Ensure unique name
        const existingTemplates = config.messageTemplates || []
        const duplicateCount = existingTemplates.filter((t, idx) => 
            t.name === template.name && idx !== templateIndex
        ).length
        if (duplicateCount > 0) {
            template.name = `${template.name} (${duplicateCount + 1})`
        }
    }

    const canSaveConfig = computed(() => {
        if (!editingConfig.value) return false
        if (editingConfig.value.name.trim() === '') return false
        if (editingConfig.value.groups.length === 0) return false
        
        // Check if we have at least one template with content, or a default message
        const hasTemplates = editingConfig.value.messageTemplates && 
            editingConfig.value.messageTemplates.length > 0 &&
            editingConfig.value.messageTemplates.some(t => t.content && t.content.trim() !== '')
        const hasDefaultMessage = editingConfig.value.defaultMessage && 
            editingConfig.value.defaultMessage.trim() !== ''
        
        return hasTemplates || hasDefaultMessage
    })

    const saveConfiguration = async () => {
        if (!canSaveConfig.value || isSavingConfiguration.value || !editingConfig.value) return

        isSavingConfiguration.value = true
        try {
            // If editing existing config, preserve the ID
            if (editingConfigIndex.value !== null && editingConfigIndex.value >= 0) {
                const existingConfig = availableConfigurations.value[editingConfigIndex.value]
                if (existingConfig && existingConfig.id) {
                    editingConfig.value.id = existingConfig.id
                }
                availableConfigurations.value[editingConfigIndex.value] = editingConfig.value
            }

            await api.whatsapp.saveConfiguration(editingConfig.value)

            // Update open configuration by ID (not name, since name can change)
            const openConfigIndex = openConfigurations.value.findIndex(c => c.id === editingConfig.value!.id)
            if (openConfigIndex >= 0) {
                openConfigurations.value[openConfigIndex] = {
                    ...editingConfig.value,
                    taglineOverride: openConfigurations.value[openConfigIndex].taglineOverride || '',
                    generatedMessage: openConfigurations.value[openConfigIndex].generatedMessage || '',
                    editedMessage: openConfigurations.value[openConfigIndex].editedMessage || '',
                    displayMessage: openConfigurations.value[openConfigIndex].displayMessage || '',
                    mediaUrl: openConfigurations.value[openConfigIndex].mediaUrl || null,
                    mediaIsImage: openConfigurations.value[openConfigIndex].mediaIsImage || false,
                    isUploadingMedia: false,
                    isSendingToQueue: openConfigurations.value[openConfigIndex].isSendingToQueue || false,
                    skipPreview: openConfigurations.value[openConfigIndex].skipPreview || false
                }
            }

            await loadConfigurations()
            closeCreateConfigModal()
            util.showToast('Configuration saved successfully!', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error: any) {
            console.error('Failed to save configuration:', error)
            const errorMsg = error.response?.data?.error || error.message || 'Failed to save configuration'
            util.showToast(`Failed to save configuration: ${errorMsg}`, { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            isSavingConfiguration.value = false
        }
    }

    const closeCreateConfigModal = () => {
        showCreateConfigModal.value = false
        editingConfig.value = null
        editingConfigIndex.value = null
    }

    return {
        availableConfigurations,
        selectedConfig,
        isLoadingConfiguration,
        isSavingConfiguration,
        openConfigurations,
        showCreateConfigModal,
        editingConfig,
        editingConfigIndex,
        configGroupsExpanded,
        configToConfigWithExtras,
        getConfigGroupsCount,
        getAccessibleGroupsCount,
        getUnaccessibleGroupsCount,
        toggleConfigGroups,
        loadConfigurations,
        addSelectedConfiguration,
        resetSelectedConfiguration,
        editSelectedConfiguration,
        deleteSelectedConfiguration,
        closeConfiguration,
        openCreateConfigModal,
        editConfiguration,
        addGroupToConfig,
        removeGroupFromConfig,
        addTemplateToConfig,
        removeTemplateFromConfig,
        ensureTemplateName,
        canSaveConfig,
        saveConfiguration,
        closeCreateConfigModal
    }
}

